import { useCallback, useEffect, useState } from 'react';
import { PageHeader, EmptyState, TableSkeleton } from '../components/common/PageHeader';
import { SearchFilterBar } from '../components/common/SearchFilterBar';
import { PaginationControls } from '../components/common/PaginationControls';
import { Modal, DeleteConfirmModal } from '../components/common/Modal';
import { StockBadge, FeaturedBadge } from '../components/common/StatusBadge';
import ProductForm, {
  emptyProductForm,
  formToProductFormData,
  productToForm,
} from '../components/forms/ProductForm';
import { listProducts, createProduct, updateProduct, deleteProduct } from '../services/productService';
import { listCategories } from '../services/categoryService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';
import { formatCurrency } from '../utils/format';

export default function ProductsPage() {
  const { showToast } = useToast();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [form, setForm] = useState(emptyProductForm);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (stockFilter !== 'all') params.inStock = stockFilter;
      const res = await listProducts(params);
      setProducts(res.data || []);
      setMeta(res.meta || { total: 0, totalPages: 1 });
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, search, categoryFilter, stockFilter, showToast]);

  useEffect(() => {
    listCategories()
      .then((res) => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyProductForm);
    setModalOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm(productToForm(product));
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || form.price === '') {
      showToast('Name and price are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = formToProductFormData(form);
      if (editing) {
        await updateProduct(editing._id, payload);
        showToast('Product updated successfully');
      } else {
        await createProduct(payload);
        showToast('Product created successfully');
      }
      setModalOpen(false);
      fetchProducts();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    try {
      await deleteProduct(deletingItem._id);
      showToast('Product deleted successfully');
      setDeleteOpen(false);
      fetchProducts();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Products"
        description="Manage your product catalog"
        action={
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + Add Product
          </button>
        }
      />

      <div className="card">
        <SearchFilterBar
          search={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Search products..."
          filters={[
            {
              key: 'category',
              label: 'Categories',
              options: categories.map((c) => ({ value: c._id, label: c.name })),
            },
            {
              key: 'stock',
              label: 'Stock',
              options: [
                { value: 'true', label: 'In Stock' },
                { value: 'false', label: 'Out of Stock' },
              ],
            },
          ]}
          filterValues={{ category: categoryFilter, stock: stockFilter }}
          onFilterChange={(key, value) => {
            if (key === 'category') setCategoryFilter(value);
            if (key === 'stock') setStockFilter(value);
            setPage(1);
          }}
          onClear={() => { setSearch(''); setCategoryFilter('all'); setStockFilter('all'); setPage(1); }}
        />

        {loading ? (
          <TableSkeleton />
        ) : products.length === 0 ? (
          <EmptyState title="No products found" description="Create your first product to get started." action={{ label: 'Add Product', onClick: openCreate }} />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product._id}>
                      <td>
                        <div className="cell-product">
                          {product.images?.[0] && (
                            <img src={product.images[0]} alt="" className="cell-thumb" />
                          )}
                          <div>
                            <p className="cell-title">{product.name}</p>
                            <p className="cell-sub">{product.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td>{product.category?.name || '—'}</td>
                      <td>{formatCurrency(product.price)}</td>
                      <td><StockBadge inStock={product.inStock} /></td>
                      <td><FeaturedBadge featured={product.featured} /></td>
                      <td>
                        <div className="action-btns">
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(product)}>Edit</button>
                          <button type="button" className="btn btn-ghost btn-sm text-danger" onClick={() => { setDeletingItem(product); setDeleteOpen(true); }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={page} totalPages={meta.totalPages} totalItems={meta.total} onPageChange={setPage} />
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Product' : 'Create Product'}
        description={editing ? 'Update product details' : 'Add a new product to your catalog'}
        size="lg"
      >
        <form onSubmit={handleSave}>
          <ProductForm form={form} onChange={setForm} categories={categories} />
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update Product' : 'Create Product'}
            </button>
          </div>
        </form>
      </Modal>

      <DeleteConfirmModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        itemName={deletingItem?.name}
        loading={deleting}
      />
    </div>
  );
}
